import React, { ReactElement, useEffect, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";

/**
 * BreakpointDebugger - A development tool to display current MUI breakpoint and viewport size
 * 
 * Usage:
 *   <BreakpointDebugger />
 * 
 * This component should only be used within the DebugProvider which ensures
 * it's only rendered in development environments.
 */
export function BreakpointDebugger(): ReactElement {
    const theme = useTheme();
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

    // Check each breakpoint
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMd = useMediaQuery(theme.breakpoints.only('md'));
    const isLg = useMediaQuery(theme.breakpoints.only('lg'));
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));

    // Determine current breakpoint
    let currentBreakpoint = 'unknown';
    if (isXs) currentBreakpoint = 'xs';
    else if (isSm) currentBreakpoint = 'sm';
    else if (isMd) currentBreakpoint = 'md';
    else if (isLg) currentBreakpoint = 'lg';
    else if (isXl) currentBreakpoint = 'xl';

    // Update viewport size on resize
    useEffect(() => {
        const updateSize = () => {
            setViewportSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Get breakpoint values from theme
    const breakpointValues = {
        xs: theme.breakpoints.values.xs,
        sm: theme.breakpoints.values.sm,
        md: theme.breakpoints.values.md,
        lg: theme.breakpoints.values.lg,
        xl: theme.breakpoints.values.xl,
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: 1.5,
                borderRadius: 1,
                zIndex: 9999,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                minWidth: 200,
                boxShadow: 3,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                Breakpoint: <span style={{ color: '#4caf50' }}>{currentBreakpoint.toUpperCase()}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                Viewport: {viewportSize.width} × {viewportSize.height}px
            </Typography>
            <Box sx={{ marginTop: 1, paddingTop: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                    Breakpoints:
                </Typography>
                {Object.entries(breakpointValues).map(([key, value]) => (
                    <Typography
                        key={key}
                        variant="body2"
                        sx={{
                            fontSize: '0.65rem',
                            color: currentBreakpoint === key ? '#4caf50' : 'rgba(255,255,255,0.6)',
                            fontWeight: currentBreakpoint === key ? 'bold' : 'normal',
                        }}
                    >
                        {key}: {value}px
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
